import {Link} from '@primer/react'
import {Fragment, type ReactElement} from 'react'

import {LABELS} from '../constants/labels'
import styles from './IssueCommentLink.module.css'

type IssueCommentCopyRenderingProps<T> = {
  queryRefs: T[]
  rollup: boolean
  label: string
  getFragmentIdentifier: (queryRef: T) => string
  renderLink: (queryRef: T) => ReactElement
  linkPrefix?: string
}

export function IssueCommentCopyRendering<T>({
  queryRefs,
  rollup,
  label,
  renderLink,
  getFragmentIdentifier,
  linkPrefix,
}: IssueCommentCopyRenderingProps<T>): ReactElement | null {
  if (!queryRefs.length) {
    return null
  }

  const linkPrefixText = linkPrefix ?? ''

  if (rollup && queryRefs.length > 1) {
    return (
      <>
        {label}{' '}
        <CommentLinkList
          queryRefs={queryRefs}
          renderLink={renderLink}
          getFragmentIdentifier={getFragmentIdentifier}
          linkPrefix={linkPrefixText}
        />
      </>
    )
  }

  const [firstQueryRef] = queryRefs

  if (!firstQueryRef) {
    return null
  }

  return (
    <>
      {label} {linkPrefixText}
      {renderLink(firstQueryRef)}
    </>
  )
}

type CommentLinkListProps<T> = {
  queryRefs: T[]
  renderLink: (queryRef: T) => ReactElement
  getFragmentIdentifier: (queryRef: T) => string
  linkPrefix: string
}

const CommentLinkList = <T,>({
  queryRefs,
  renderLink,
  getFragmentIdentifier,
  linkPrefix,
}: CommentLinkListProps<T>): ReactElement => (
  <>
    {queryRefs.map((queryRef, index) => (
      <Fragment key={getFragmentIdentifier(queryRef)}>
        {index > 0 && renderSeparator(index, queryRefs.length)}
        {linkPrefix}
        {renderLink(queryRef)}
      </Fragment>
    ))}
  </>
)

const renderSeparator = (index: number, total: number) => {
  if (index === total - 1) {
    return ` ${LABELS.timeline.and} `
  }

  return ', '
}

type IssueCommentLinkProps = {
  linkText: string
  url: string | null | undefined
  testId: string
}

export const IssueCommentLink = ({linkText, url, testId}: IssueCommentLinkProps): ReactElement => {
  if (url) {
    return (
      <Link href={url} inline className={styles.commentLink} data-testid={testId}>
        {linkText}
      </Link>
    )
  }

  return (
    <span className={styles.commentLink} data-testid={testId}>
      {linkText}
    </span>
  )
}
